import { useState } from "react";
import {
  DISPLAY,
  DISPLAY_SOURCE_BYO,
  DISPLAY_SOURCE_OPTIONS,
  LIMITS,
  LANGUAGES,
  WIZARD_TEMPLATES,
  BYO_TEMPLATE_ID,
} from "../constants.js";
import { uploadReference } from "../lib/references/uploadReference.js";
import { validateOffer } from "../lib/offers/validateOffer.js";
import { CheckIcon, CoinsIcon, PlusIcon, UploadIcon } from "../Components/Icons";
import "./WizardSteps.css";

export default function StepBody({
  config,
  state,
  onUpdate,
  brand,
  appConfig,
  offers,
  onSaveOffers,
  onOfferCreated,
}) {
  switch (config.kind) {
    case "offer":
      return (
        <OfferStep
          offers={offers}
          selectedId={state.offerId}
          brand={brand}
          onSaveOffers={onSaveOffers}
          onSelect={(offer) =>
            onUpdate({
              offerId: offer.id,
              // Prefill landing URL unless the user already customized it,
              // same rule as the original.
              ...(state.landingUrl === "" || state.landingUrl === state.landingUrlPrefilledFrom
                ? { landingUrl: offer.landingUrl, landingUrlPrefilledFrom: offer.landingUrl }
                : {}),
            })
          }
          onCreated={onOfferCreated}
        />
      );
    case "cards":
      // Only "display_language" is a cards-kind step right now — same as
      // the original, which also hardcodes this binding.
      return (
        <CardGrid
          options={config.options}
          selected={state.displayLanguage}
          onSelect={(value) => onUpdate({ displayLanguage: value })}
        />
      );
    case "url":
      return (
        <UrlStep value={state.landingUrl} onChange={(landingUrl) => onUpdate({ landingUrl })} />
      );
    case "display_source":
      return (
        <DisplaySourceStep
          mode={state.displaySource}
          brand={brand}
          appConfig={appConfig}
          backgroundUrl={state.backgroundUrl}
          onChange={(displaySource) => onUpdate({ displaySource })}
          onUploaded={(id, url) => onUpdate({ backgroundRefId: id, backgroundUrl: url })}
        />
      );
    case "display_sizes":
      return (
        <DisplaySizesStep
          selected={state.displaySizes}
          onChange={(displaySizes) => onUpdate({ displaySizes })}
        />
      );
    case "display_template":
      return (
        <DisplayTemplateStep
          selectedId={state.displayTemplateId}
          selectedSizes={state.displaySizes}
          onSelect={(displayTemplateId) => onUpdate({ displayTemplateId })}
        />
      );
    default:
      // Every other kind is implemented now.
      return null;
  }
}

/* ---- Generic card grid (language, display source) ------------------------ */

export function CardGrid({ options, selected, onSelect }) {
  return (
    <div className="wizard-step__card-grid" role="radiogroup">
      {options.map((option) => {
        const isSelected = selected === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={option.disabled}
            onClick={() => onSelect(option.value)}
            className={`wizard-step__card${isSelected ? " wizard-step__card--selected" : ""}`}
          >
            <div className="wizard-step__card-top">
              <span className="wizard-step__card-label">{option.label}</span>
              {option.badge ? (
                <span className="wizard-step__card-badge">{option.badge}</span>
              ) : isSelected ? (
                <CheckIcon className="wizard-step__icon wizard-step__icon--accent" />
              ) : null}
            </div>
            <p className="wizard-step__card-desc">{option.description}</p>
          </button>
        );
      })}
    </div>
  );
}

/* ---- Landing URL ----------------------------------------------------------- */

function isValidUrl(value) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function UrlStep({ value, onChange }) {
  return (
    <div>
      <label htmlFor="wizard-landing-url" className="wizard-step__label">
        Landing URL
      </label>
      <input
        id="wizard-landing-url"
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://…"
        className="wizard-step__input"
      />
      {value && !isValidUrl(value) ? (
        <p className="wizard-step__error">Enter a full URL, including https://</p>
      ) : null}
    </div>
  );
}

/* ---- Display sizes --------------------------------------------------------- */

function DisplaySizesStep({ selected, onChange }) {
  function toggle(id) {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  }

  const display = DISPLAY.SIZES.filter((s) => s.channel === "display");
  const social = DISPLAY.SIZES.filter((s) => s.channel === "social");

  function tile(size) {
    const isSelected = selected.includes(size.id);
    return (
      <button
        key={size.id}
        type="button"
        role="checkbox"
        aria-checked={isSelected}
        onClick={() => toggle(size.id)}
        className={`wizard-step__tile${isSelected ? " wizard-step__tile--selected" : ""}`}
      >
        <span>
          <span className="wizard-step__tile-id">{size.id}</span>
          <span className="wizard-step__tile-label">{size.label}</span>
        </span>
        <span className="wizard-step__tile-right">
          {size.channel === "social" ? (
            <span className="wizard-step__tile-badge wizard-step__tile-badge--gif">GIF</span>
          ) : size.core ? (
            <span className="wizard-step__tile-badge">core</span>
          ) : null}
          {isSelected ? <CheckIcon className="wizard-step__icon wizard-step__icon--accent" /> : null}
        </span>
      </button>
    );
  }

  return (
    <div className="wizard-step__sizes">
      <div>
        <p className="wizard-step__group-label">Display (HTML5)</p>
        <div className="wizard-step__tile-grid">{display.map(tile)}</div>
      </div>
      <div>
        <p className="wizard-step__group-label">Paid social — exported as GIF</p>
        <div className="wizard-step__tile-grid">{social.map(tile)}</div>
      </div>
    </div>
  );
}

/* ---- Display template ------------------------------------------------------ */

function DisplayTemplateStep({ selectedId, selectedSizes, onSelect }) {
  return (
    <div className="wizard-step__card-grid">
      {WIZARD_TEMPLATES.map((template) => {
        const supported = selectedSizes.filter((s) => template.supportedSizes.includes(s));
        const disabled = supported.length === 0;
        const isSelected = selectedId === template.id;
        return (
          <button
            key={template.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onSelect(template.id)}
            className={`wizard-step__card${isSelected ? " wizard-step__card--selected" : ""}`}
          >
            <div className="wizard-step__card-top">
              <span className="wizard-step__card-label">{template.name}</span>
              {isSelected ? <CheckIcon className="wizard-step__icon wizard-step__icon--accent" /> : null}
            </div>
            <p className="wizard-step__card-desc">{template.description}</p>
            <p
              className={`wizard-step__template-support${
                disabled
                  ? ""
                  : supported.length < selectedSizes.length
                    ? " wizard-step__template-support--partial"
                    : " wizard-step__template-support--full"
              }`}
            >
              {disabled
                ? "Supports none of your sizes"
                : `Supports ${supported.length} of ${selectedSizes.length} selected sizes`}
            </p>
          </button>
        );
      })}
    </div>
  );
}

/* ---- Review (not dispatched via StepBody - REVIEW_KEY isn't in GENERATE_WIZARD_STEPS) --- */

export function ReviewStep({
  state,
  offers,
  credits,
  creditCost,
  enoughCredits,
  canCreate,
  creating,
  createError,
  onCreate,
  onJump,
}) {
  const offer = (offers ?? []).find((o) => o.id === state.offerId);
  const byo = state.displaySource === DISPLAY_SOURCE_BYO;
  const template = WIZARD_TEMPLATES.find((t) =>
    byo ? t.id === BYO_TEMPLATE_ID : t.id === state.displayTemplateId
  );
  const effectiveSizes = template
    ? state.displaySizes.filter((s) => template.supportedSizes.includes(s))
    : state.displaySizes;

  const rows = [
    { step: "offer", label: "Offer", value: offer?.title ?? "—" },
    { step: "landing_url", label: "Landing URL", value: state.landingUrl },
    {
      step: "display_source",
      label: "Source",
      value: byo ? "Bring your own image" : "Platform-composed",
    },
    {
      step: "display_language",
      label: "Language",
      value: LANGUAGES.find((l) => l.value === state.displayLanguage)?.label ?? state.displayLanguage,
    },
    { step: "display_sizes", label: "Sizes", value: effectiveSizes.join(", ") || "—" },
    {
      step: byo ? "display_source" : "display_template",
      label: "Template",
      value: byo ? "Full Bleed (your image)" : (template?.name ?? "—"),
    },
  ];

  return (
    <div>
      <h2 className="wizard__step-title">Review the brief</h2>
      <p className="wizard__step-subtitle">This exact recipe is stored with the asset.</p>

      <dl className="wizard-review__list">
        {rows.map((row, i) => (
          <div key={i} className="wizard-review__row">
            <dt className="wizard-review__label">{row.label}</dt>
            <dd className="wizard-review__value">{row.value}</dd>
            <button type="button" onClick={() => onJump(row.step)} className="wizard-review__edit">
              Edit
            </button>
          </div>
        ))}
      </dl>

      <div className="wizard-review__footer">
        {createError ? (
          <p className="wizard-review__alert" role="alert">
            {createError}
          </p>
        ) : null}
        {/* Credit gating hidden for now — reinstate with AI generation.
        {!enoughCredits ? (
          <p className="wizard-review__alert wizard-review__alert--credit">
            This generation costs {creditCost} credit{creditCost === 1 ? "" : "s"} — you have{" "}
            {credits}.
          </p>
        ) : null}
        */}
        <div className="wizard-review__create-row">
          {/* Credit cost line hidden for now — reinstate with AI generation.
          <span className="wizard-review__cost">
            <CoinsIcon className="wizard-step__icon" />
            Costs {creditCost} credit{creditCost === 1 ? "" : "s"} · you have {credits}
          </span>
          */}
          <button
            type="button"
            onClick={onCreate}
            disabled={!canCreate || creating}
            className="btn btn--primary wizard-review__create-btn"
          >
            {creating ? (
              <>
                <span className="wizard-step__spinner" />
                Generating…
              </>
            ) : (
              "Create asset"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Offer (pick existing or create inline) -------------------------------- */

function OfferStep({ offers, selectedId, brand, onSaveOffers, onSelect, onCreated }) {
  const items = offers ?? [];
  const [showCreate, setShowCreate] = useState(items.length === 0);
  const [title, setTitle] = useState("");
  const [landingUrl, setLandingUrl] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validateOffer({ title, landingUrl });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setPending(true);

    const newOffer = {
      id: `offer_${crypto.randomUUID().slice(0, 8)}`,
      title: title.trim(),
      description: description.trim(),
      landingUrl: landingUrl.trim(),
      active: true,
      createdAt: new Date().toISOString(),
    };

    try {
      await onSaveOffers(brand.id, [...items, newOffer]);
      onCreated(newOffer);
      onSelect(newOffer);
      setShowCreate(false);
    } catch (err) {
      setError("Couldn't create the offer. Please try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="wizard-step__offer">
      {items.length > 0 ? (
        <div className="wizard-step__offer-list" role="radiogroup" aria-label="Offers">
          {items.map((offer) => {
            const isSelected = selectedId === offer.id;
            return (
              <button
                key={offer.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onSelect(offer)}
                className={`wizard-step__offer-item${
                  isSelected ? " wizard-step__offer-item--selected" : ""
                }`}
              >
                <div className="wizard-step__card-top">
                  <span className="wizard-step__card-label">{offer.title}</span>
                  <span className="wizard-step__offer-item-right">
                    {!offer.active ? (
                      <span className="wizard-step__card-badge">Paused</span>
                    ) : null}
                    {isSelected ? (
                      <CheckIcon className="wizard-step__icon wizard-step__icon--accent" />
                    ) : null}
                  </span>
                </div>
                <p className="wizard-step__offer-url">{offer.landingUrl}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="wizard-step__offer-empty">This brand has no offers yet — add one below.</p>
      )}

      {showCreate ? (
        <form onSubmit={handleSubmit} className="wizard-step__offer-form">
          {error ? <p className="wizard-step__error">{error}</p> : null}
          <div className="wizard-step__offer-form-grid">
            <label className="wizard-step__field">
              <span>Title</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="e.g. Spring Sale — 20% off"
              />
            </label>
            <label className="wizard-step__field">
              <span>Landing URL</span>
              <input
                type="url"
                value={landingUrl}
                onChange={(e) => setLandingUrl(e.target.value)}
                placeholder="https://…"
              />
            </label>
          </div>
          <label className="wizard-step__field">
            <span>Description</span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              placeholder="Optional one-liner"
            />
          </label>
          <button type="submit" disabled={pending} className="btn btn--primary wizard-step__offer-submit">
            <PlusIcon className="wizard-step__icon" />
            {pending ? "Adding…" : "Add and select"}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="wizard-step__offer-new-btn"
        >
          <PlusIcon className="wizard-step__icon" />
          New offer
        </button>
      )}
    </div>
  );
}

/* ---- Display source (platform vs. bring-your-own) ------------------------- */

function DisplaySourceStep({ mode, brand, appConfig, backgroundUrl, onChange, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const byo = mode === DISPLAY_SOURCE_BYO;

  async function handleUpload(file) {
    if (!file) return;
    setUploadError(null);

    if (!file.type.startsWith("image/")) {
      setUploadError("Only image files are supported (PNG, JPG, WebP, SVG, GIF).");
      return;
    }
    if (file.size > LIMITS.MAX_UPLOAD_MB * 1024 * 1024) {
      setUploadError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${LIMITS.MAX_UPLOAD_MB} MB.`
      );
      return;
    }

    setUploading(true);
    try {
      // Reusing the References upload pipeline (kind: "example") rather than
      // a third upload mechanism — see the note where this step was built.
      // Deliberately not persisted to the brand's permanent references list;
      // this is provisional until an asset actually exists.
      const { referenceId, url } = await uploadReference(
        appConfig,
        { brandId: brand.id, kind: "example", file },
        () => {},
        () => {}
      );
      onUploaded(referenceId, url);
    } catch (err) {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="wizard-step__source">
      <CardGrid options={DISPLAY_SOURCE_OPTIONS} selected={mode} onSelect={onChange} />

      {byo ? (
        <div className="wizard-step__upload-box">
          <p className="wizard-step__upload-title">Upload your creative (optional now)</p>
          <p className="wizard-step__upload-desc">
            Uses the full-bleed frame; text is treated as part of your image (you can un-bake any
            field in the composer). You can also upload later while composing.
          </p>

          {uploadError ? <p className="wizard-step__error">{uploadError}</p> : null}

          <div className="wizard-step__upload-row">
            <label className={`wizard-step__upload-label${uploading ? " wizard-step__upload-label--disabled" : ""}`}>
              {uploading ? (
                <>
                  <span className="wizard-step__spinner" />
                  Uploading…
                </>
              ) : (
                <>
                  <UploadIcon className="wizard-step__icon" />
                  {backgroundUrl ? "Replace image" : "Upload background"}
                </>
              )}
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className="wizard-step__file-input"
                onChange={(e) => {
                  handleUpload(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            {backgroundUrl ? (
              <img src={backgroundUrl} alt="Uploaded background" className="wizard-step__preview" />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}